import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Calculator,
  CheckCircle2,
  Clock3,
  Eye,
  IndianRupee,
  RotateCcw,
  Save,
  Sigma,
} from "lucide-react";
import { AuthContext } from "../../Context/AuthContext";
import { useToast } from "../../Context/ToastContext";
import API from "../../../services/api";
import { v2ValidateFormula } from "./SalaryApiV2";
import { entSimulateFormula } from "./salaryEnterpriseApi";
import { defaultExtraWorkConfig, OVERTIME_WORKS_PATH } from "./extraWorkPolicy";
import {
  OT_FORMULA_EXAMPLE,
  OT_FORMULA_FUNCTIONS,
  OT_FORMULA_OPERATORS,
  OT_FORMULA_TEMPLATES,
  OT_FORMULA_VARIABLES,
  OT_FORMULA_VARIABLE_CODES,
  evaluateOtFormula,
  explainWithExpression,
  formatInr,
  matchOtFormulaTemplate,
  readableOtFormula,
  sampleToContext,
  templateById,
} from "./otFormulaModel";
import "../../../styles/overtime-formula-builder.css";

const GROUPS = ["Salary", "Overtime", "Calendar"];

function mergeConfig(raw) {
  const cfg = raw && typeof raw === "object" ? raw : {};
  return {
    ...defaultExtraWorkConfig(),
    ...cfg,
    overtime: {
      ...defaultExtraWorkConfig().overtime,
      ...(cfg.overtime || {}),
    },
    sunday: { ...defaultExtraWorkConfig().sunday, ...(cfg.sunday || {}) },
    holiday: { ...defaultExtraWorkConfig().holiday, ...(cfg.holiday || {}) },
    premium: { ...defaultExtraWorkConfig().premium, ...(cfg.premium || {}) },
    comp_off: { ...defaultExtraWorkConfig().comp_off, ...(cfg.comp_off || {}) },
    eligibility: { ...defaultExtraWorkConfig().eligibility, ...(cfg.eligibility || {}) },
    processing: { ...defaultExtraWorkConfig().processing, ...(cfg.processing || {}) },
  };
}

function Field({ label, hint, children }) {
  return (
    <label className="ofb-field">
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

export default function OvertimeFormulaBuilderPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const editorRef = useRef(null);
  const { user: me } = useContext(AuthContext);
  const role = (me?.role || me?.user?.role || "").toLowerCase();
  const canEdit = role === "admin" || role === "hr";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [config, setConfig] = useState(() => mergeConfig(location.state?.extraWork));
  const [savedExpression, setSavedExpression] = useState("");
  const [templateId, setTemplateId] = useState("gross");
  const [expr, setExpr] = useState(templateById("gross").expression);
  const [sample, setSample] = useState(OT_FORMULA_EXAMPLE);
  const [serverPreview, setServerPreview] = useState(null);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get("/organisation/me/extra-work-config");
        if (cancelled) return;
        const fromApi = mergeConfig(res.data?.extra_work);
        const fromNav = location.state?.extraWork ? mergeConfig(location.state.extraWork) : null;
        const base = fromNav || fromApi;
        const expression = String(base.overtime?.formula_expression || "").trim();
        const savedTpl = String(base.overtime?.formula_template || "");
        const active = expression ? savedTpl || matchOtFormulaTemplate(expression) : "gross";
        setConfig(base);
        setSavedExpression(String(fromApi.overtime?.formula_expression || "").trim());
        setTemplateId(OT_FORMULA_TEMPLATES.some((t) => t.id === active) ? active : "custom");
        setExpr(expression || templateById("gross").expression);
        setSample({
          ...OT_FORMULA_EXAMPLE,
          workingDays: String(base.overtime?.working_days ?? OT_FORMULA_EXAMPLE.workingDays),
          workingHours: String(base.overtime?.working_hours ?? OT_FORMULA_EXAMPLE.workingHours),
          otPayRate: String(base.overtime?.ot_pay_rate_pct ?? OT_FORMULA_EXAMPLE.otPayRate),
          fixedAmount:
            Number(base.overtime?.fixed_amount) > 0
              ? String(base.overtime.fixed_amount)
              : OT_FORMULA_EXAMPLE.fixedAmount,
        });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Could not load the overtime formula");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.state, toast]);

  const context = useMemo(() => sampleToContext(sample), [sample]);
  const live = useMemo(() => evaluateOtFormula(expr, context), [expr, context]);
  const steps = useMemo(
    () => (live.ok ? explainWithExpression(templateId, expr, context, live.value) : []),
    [live, templateId, expr, context]
  );
  const groups = useMemo(
    () => GROUPS.map((group) => ({ group, items: OT_FORMULA_VARIABLES.filter((v) => v.group === group) })),
    []
  );

  function patchSample(key, value) {
    setSample((prev) => ({ ...prev, [key]: value }));
    setServerPreview(null);
    setServerError("");
  }

  function selectTemplate(id) {
    const tpl = templateById(id);
    if (!tpl) return;
    setTemplateId(id);
    if (id !== "custom") setExpr(tpl.expression);
    setServerPreview(null);
    setServerError("");
  }

  function insertToken(token) {
    const el = editorRef.current;
    const value = expr || "";
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const edge = "()+-*/,";
    const left = before.slice(-1);
    const right = after.slice(0, 1);
    const padBefore = before && !edge.includes(left) && !edge.includes(token) && !/\s$/.test(before) ? " " : "";
    const padAfter = after && !edge.includes(right) && !edge.includes(token) && !/^\s/.test(after) ? " " : "";
    const next = `${before}${padBefore}${token}${padAfter}${after}`;
    setExpr(next);
    setTemplateId(matchOtFormulaTemplate(next));
    setServerPreview(null);
    const caret = (before + padBefore + token).length;
    requestAnimationFrame(() => {
      editorRef.current?.focus();
      editorRef.current?.setSelectionRange(caret, caret);
    });
  }

  function onExpressionChange(value) {
    setExpr(value);
    setTemplateId(matchOtFormulaTemplate(value));
    setServerPreview(null);
    setServerError("");
  }

  function applyExample() {
    setTemplateId("gross");
    setExpr(templateById("gross").expression);
    setSample(OT_FORMULA_EXAMPLE);
    setServerPreview(null);
    setServerError("");
  }

  function onReset() {
    const expression = savedExpression || templateById("gross").expression;
    setExpr(expression);
    setTemplateId(savedExpression ? matchOtFormulaTemplate(savedExpression) : "gross");
    setSample({
      ...OT_FORMULA_EXAMPLE,
      workingDays: String(config.overtime?.working_days ?? OT_FORMULA_EXAMPLE.workingDays),
      workingHours: String(config.overtime?.working_hours ?? OT_FORMULA_EXAMPLE.workingHours),
      otPayRate: String(config.overtime?.ot_pay_rate_pct ?? OT_FORMULA_EXAMPLE.otPayRate),
      fixedAmount:
        Number(config.overtime?.fixed_amount) > 0
          ? String(config.overtime.fixed_amount)
          : OT_FORMULA_EXAMPLE.fixedAmount,
    });
    setServerPreview(null);
    setServerError("");
  }

  function goBack() {
    navigate(OVERTIME_WORKS_PATH, {
      state: {
        otFormulaDraft: {
          expression: String(expr || "").trim(),
          templateId,
        },
      },
    });
  }

  async function runServerPreview() {
    const expression = String(expr || "").trim();
    if (!live.ok) {
      setServerError(live.error || "Fix the formula before previewing.");
      setServerPreview(null);
      return null;
    }
    setPreviewing(true);
    setServerError("");
    try {
      const validation = await v2ValidateFormula({
        expression,
        known_identifiers: OT_FORMULA_VARIABLE_CODES,
        strict_unknown_identifiers: true,
      });
      if (!validation?.is_valid) {
        const message = validation?.error || "Formula is not valid";
        setServerError(message);
        toast.error(message);
        return null;
      }
      const simulated = await entSimulateFormula({
        expression,
        sample_context: context,
      });
      const serverValue = roundServer(simulated?.result);
      setServerPreview({ value: serverValue, dependencies: validation.dependencies || [] });
      if (serverValue != null && Math.abs(serverValue - live.value) > 0.01) {
        toast.error("Preview does not match the payroll formula engine. Check the formula.");
      } else {
        toast.success("Payroll engine confirmed this OT amount.");
      }
      return simulated;
    } catch (err) {
      const message = err?.detail || err?.error || err?.message || "Preview failed";
      setServerError(typeof message === "string" ? message : "Preview failed");
      toast.error(typeof message === "string" ? message : "Preview failed");
      return null;
    } finally {
      setPreviewing(false);
    }
  }

  async function onSave() {
    if (!canEdit) {
      toast.error("You do not have permission to save this formula.");
      return;
    }
    const expression = String(expr || "").trim();
    if (!live.ok) {
      toast.error(live.error || "Fix the formula before saving.");
      return;
    }
    const confirmed = await runServerPreview();
    if (!confirmed) return;
    const overtime = {
      ...config.overtime,
      method: "formula",
      formula_expression: expression,
      formula_template: templateId,
      working_days: Number(sample.workingDays) || 0,
      working_hours: Number(sample.workingHours) || 0,
      ot_pay_rate_pct: Number(sample.otPayRate) || 0,
      fixed_amount: /\bFIXED_OT_AMOUNT\b/i.test(expression)
        ? Number(sample.fixedAmount) || 0
        : config.overtime?.fixed_amount,
    };
    const payload = { ...config, overtime };
    try {
      setSaving(true);
      const res = await API.put("/organisation/me/extra-work-config", { extra_work: payload });
      const saved = mergeConfig(res.data?.extra_work || payload);
      setConfig(saved);
      setSavedExpression(String(saved.overtime?.formula_expression || expression));
      setExpr(String(saved.overtime?.formula_expression || expression));
      toast.success("OT formula saved. Payroll will use it for approved overtime.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = Array.isArray(detail) ? detail[0] : detail;
      toast.error(typeof message === "string" ? message : "Could not save the OT formula");
    } finally {
      setSaving(false);
    }
  }

  const busy = loading || saving || previewing;
  const amountLabel = live.ok ? formatInr(live.value) : "—";

  return (
    <div className="ofb">
      <header className="ofb-hero">
        <div className="ofb-hero-copy">
          <p className="ofb-kicker">Brixigo Payroll · Overtime only</p>
          <h1>Overtime Formula Builder</h1>
          <p>
            Build the formula that turns approved OT hours into pay. Attendance still records the hours.
            Payroll evaluates this expression with the salary formula engine.
          </p>
        </div>
        <div className="ofb-actions">
          <button type="button" className="ofb-btn ofb-btn--ghost" onClick={goBack}>
            <ArrowLeft size={16} /> Back to Overtime Settings
          </button>
          <button type="button" className="ofb-btn ofb-btn--ghost" onClick={onReset} disabled={busy}>
            <RotateCcw size={16} /> Reset
          </button>
          <button type="button" className="ofb-btn ofb-btn--line" onClick={runServerPreview} disabled={busy || !canEdit}>
            <Eye size={16} /> Preview Calculation
          </button>
          <button type="button" className="ofb-btn ofb-btn--primary" onClick={onSave} disabled={busy || !canEdit}>
            <Save size={16} /> {saving ? "Saving…" : "Save Formula"}
          </button>
        </div>
      </header>

      <section className="ofb-example" aria-label="Worked example">
        <Calculator size={18} />
        <p>
          <strong>Example.</strong> Gross Salary ₹30,000, working days 30, working hours/day 10, OT hours 15,
          OT rate 100% → regular hourly rate ₹100 → OT amount <strong>₹1,500</strong>.
        </p>
        <button type="button" onClick={applyExample}>
          Load this example
        </button>
      </section>

      <section className="ofb-templates" aria-label="OT formula templates">
        {OT_FORMULA_TEMPLATES.map((tpl) => {
          const selected = templateId === tpl.id;
          const previewCtx = sampleToContext(sample);
          const preview = tpl.id === "custom"
            ? (selected ? live : { ok: false, value: null })
            : evaluateOtFormula(tpl.expression, previewCtx);
          return (
            <button
              key={tpl.id}
              type="button"
              className={`ofb-template${selected ? " is-selected" : ""}`}
              aria-pressed={selected}
              onClick={() => selectTemplate(tpl.id)}
            >
              <span className="ofb-template-kicker">{selected ? "Selected" : "Template"}</span>
              <strong>{tpl.label}</strong>
              <span className="ofb-template-summary">{tpl.summary}</span>
              <span className="ofb-template-formula">{tpl.plain}</span>
              <span className="ofb-template-amount">
                {preview.ok ? formatInr(preview.value) : "—"}
              </span>
            </button>
          );
        })}
      </section>

      <div className="ofb-layout">
        <section className="ofb-card ofb-editor-card">
          <header className="ofb-card-head">
            <Sigma size={18} />
            <div>
              <h2>Visual formula</h2>
              <p>Click a variable or operator to insert it, or type the expression directly.</p>
            </div>
          </header>

          <div className="ofb-readable" aria-live="polite">
            <span>Reads as</span>
            <strong>{readableOtFormula(expr)}</strong>
          </div>

          <textarea
            ref={editorRef}
            className="ofb-editor"
            value={expr}
            spellCheck={false}
            disabled={!canEdit || loading}
            aria-label="OT formula expression"
            placeholder="(GROSS / WORKING_DAYS / WORKING_HOURS) * OT_HOURS * (OT_PAY_RATE / 100)"
            onChange={(e) => onExpressionChange(e.target.value)}
          />
          {live.error ? (
            <p className="ofb-error" role="alert">
              <AlertCircle size={14} /> {live.error}
            </p>
          ) : (
            <p className="ofb-ok">
              <CheckCircle2 size={14} /> Formula is ready for a live preview.
            </p>
          )}

          {groups.map((group) => (
            <div key={group.group} className="ofb-var-block">
              <h3>{group.group}</h3>
              <div className="ofb-chips">
                {group.items.map((variable) => (
                  <button
                    key={variable.code}
                    type="button"
                    className="ofb-chip"
                    disabled={!canEdit}
                    onClick={() => insertToken(variable.code)}
                  >
                    <strong>{variable.label}</strong>
                    <em>{variable.code}</em>
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="ofb-var-block">
            <h3>Operators</h3>
            <div className="ofb-ops">
              {OT_FORMULA_OPERATORS.map((op) => (
                <button key={op.token} type="button" disabled={!canEdit} onClick={() => insertToken(op.token)}>
                  {op.label}
                </button>
              ))}
              {OT_FORMULA_FUNCTIONS.map((fn) => (
                <button key={fn} type="button" className="ofb-fn" disabled={!canEdit} onClick={() => insertToken(`${fn}(`)}>
                  {fn}()
                </button>
              ))}
            </div>
          </div>
        </section>

        <aside className="ofb-side">
          <section className="ofb-card">
            <header className="ofb-card-head">
              <IndianRupee size={18} />
              <div>
                <h2>Sample inputs</h2>
                <p>Change any value. The OT amount updates immediately.</p>
              </div>
            </header>
            <div className="ofb-inputs">
              <Field label="Basic Salary">
                <input type="number" min="0" step="0.01" value={sample.basic} onChange={(e) => patchSample("basic", e.target.value)} />
              </Field>
              <Field label="Gross Salary">
                <input type="number" min="0" step="0.01" value={sample.gross} onChange={(e) => patchSample("gross", e.target.value)} />
              </Field>
              <Field label="DA">
                <input type="number" min="0" step="0.01" value={sample.da} onChange={(e) => patchSample("da", e.target.value)} />
              </Field>
              <Field label="Hourly Rate" hint="Used by Hourly Rate-Based OT">
                <input type="number" min="0" step="0.01" value={sample.hourlyRate} onChange={(e) => patchSample("hourlyRate", e.target.value)} />
              </Field>
              <Field label="Fixed amount / OT hour">
                <input type="number" min="0" step="0.01" value={sample.fixedAmount} onChange={(e) => patchSample("fixedAmount", e.target.value)} />
              </Field>
              <Field label="OT Hours">
                <input type="number" min="0" step="0.25" value={sample.otHours} onChange={(e) => patchSample("otHours", e.target.value)} />
              </Field>
              <Field label="Working Days">
                <input type="number" min="0" step="1" value={sample.workingDays} onChange={(e) => patchSample("workingDays", e.target.value)} />
              </Field>
              <Field label="Working Hours / Day">
                <input type="number" min="0" step="0.5" value={sample.workingHours} onChange={(e) => patchSample("workingHours", e.target.value)} />
              </Field>
              <Field label="OT Pay Rate (%)">
                <input type="number" min="0" step="1" value={sample.otPayRate} onChange={(e) => patchSample("otPayRate", e.target.value)} />
              </Field>
            </div>
          </section>

          <section className="ofb-result" aria-live="polite">
            <div className="ofb-result-top">
              <Clock3 size={16} />
              <span>Live OT amount</span>
            </div>
            <p className="ofb-result-amount">{amountLabel}</p>
            <p className="ofb-result-formula">{readableOtFormula(expr)}</p>
            {serverPreview ? (
              <p className="ofb-server">
                Payroll engine: {formatInr(serverPreview.value)}
              </p>
            ) : null}
            {serverError ? <p className="ofb-error">{serverError}</p> : null}
          </section>

          <section className="ofb-card">
            <header className="ofb-card-head">
              <Calculator size={18} />
              <div>
                <h2>Calculation breakdown</h2>
                <p>Every step uses the sample inputs on this page.</p>
              </div>
            </header>
            {steps.length ? (
              <ol className="ofb-steps">
                {steps.map((step) => (
                  <li key={step.label} className={step.emphasize ? "is-total" : undefined}>
                    <span>{step.label}</span>
                    <strong>{step.value}</strong>
                    {step.note ? <small>{step.note}</small> : null}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="ofb-muted">Enter a valid formula to see the breakdown.</p>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

function roundServer(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}
